import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCommercialDesignProjects } from "../useHook"
import { FiArrowLeft } from 'react-icons/fi'
import Swal from 'sweetalert2'

const OurProjectCommercialDesignDetails = () => {
    const navigate = useNavigate()
    const { id } = useParams()
    const { getProjectByIdData, showProjectOnHomeScreen } = useCommercialDesignProjects()
    const [projectData, setProjectData] = useState(null)
    const [isHomeScreenLoading, setIsHomeScreenLoading] = useState(false)
    const [selectedImage, setSelectedImage] = useState(null)
    const [isImageModalOpen, setIsImageModalOpen] = useState(false)

    useEffect(() => {
        getProjectByIdData(id, setProjectData)
    }, [id])

    const handleHomeScreenItem = async () => {
        if (!projectData?.id) return;

        setIsHomeScreenLoading(true);
        const newStatus = !projectData.homeScreen;

        try {
            await showProjectOnHomeScreen(projectData.id, newStatus ? 1 : 0);
            await getProjectByIdData(id, setProjectData);
            Swal.fire({
                title: "Success",
                text: newStatus
                    ? "Project has been added to home screen"
                    : "Project has been removed from home screen",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error("Failed to update home screen status:", error);
            Swal.fire({
                title: "Error",
                text: "Failed to update home screen status. Please try again.",
                icon: "error",
                timer: 2000,
                showConfirmButton: false,
            });
        } finally {
            setIsHomeScreenLoading(false);
        }
    };

    const openImageModal = (imageUrl) => {
        setSelectedImage(imageUrl);
        setIsImageModalOpen(true);
    };

    const closeImageModal = () => {
        setSelectedImage(null);
        setIsImageModalOpen(false);
    };

    return (
        <>
            {
                !projectData && (
                    <div className="container mx-auto px-4 py-8">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-gray-800 mb-4">
                                Commercial design project not found
                            </h2>
                            <button
                                onClick={() => navigate("/our-project-commercial-design-categories")}
                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                            >
                                <FiArrowLeft className="w-5 h-5" />
                                Back to Commercial Design Projects
                            </button>
                        </div>
                    </div>
                )}
            {
                projectData && (
                    <div className="container mx-auto px-4 py-8">
                        <div className="mb-6 flex justify-between items-center">
                            <div className="flex gap-4">
                                {/* <button
                                    className={`py-2 px-4 rounded flex items-center space-x-2 ${isHomeScreenLoading
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-blue-500 hover:bg-blue-600"
                                        } text-white`}
                                    onClick={handleHomeScreenItem}
                                    disabled={isHomeScreenLoading}
                                >
                                    {isHomeScreenLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>Updating...</span>
                                        </>
                                    ) : (
                                        <span>
                                            {projectData?.homeScreen
                                                ? "Hide from home screen"
                                                : "Home page Priority"}
                                        </span>
                                    )}
                                </button> */}
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            {/* Header Image */}
                            {projectData.headerImage && (
                                <div className="w-full h-64 md:h-96">
                                    <img
                                        src={projectData.headerImage}
                                        alt={projectData.name || projectData.title}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                </div>
                            )}

                            <div className="p-6">
                                {/* Project Title */}
                                <h1 className="text-3xl font-bold text-gray-800 mb-4">
                                    {projectData.name || projectData.title}
                                </h1>

                                {/* Project Meta Information */}
                                <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600">
                                    {projectData.category && (
                                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                                            {projectData.category.name}
                                        </span>
                                    )}
                                    {projectData.status && (
                                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full">
                                            {projectData.status}
                                        </span>
                                    )}
                                    {projectData.created_at && (
                                        <span className="text-gray-500">
                                            Created: {new Date(projectData.created_at).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>

                                {/* Project Description */}
                                {projectData.description && (
                                    <div className="mb-6">
                                        <h2 className="text-xl font-semibold text-gray-800 mb-2">Description</h2>
                                        <p className="text-gray-700 leading-relaxed">{projectData.description}</p>
                                    </div>
                                )}

                                {/* Project Images Gallery */}
                                {projectData.projectImages && projectData.projectImages.length > 0 && (
                                    <div className="mb-6">
                                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Project Gallery</h2>
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                            {projectData.projectImages.map((image, index) => (
                                                <div
                                                    key={image.id || index}
                                                    className="relative group cursor-pointer"
                                                    onClick={() => openImageModal(image.image_url)}
                                                >
                                                    <img
                                                        src={image.image_url}
                                                        alt={`Project image ${index + 1}`}
                                                        className="w-full h-48 object-cover rounded-lg shadow-md transition-transform group-hover:scale-105"
                                                        loading="lazy"
                                                    />
                                                    {image.is_primary && (
                                                        <span className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded shadow">
                                                            Primary
                                                        </span>
                                                    )}
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 rounded-lg flex items-center justify-center">
                                                        <svg
                                                            className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Project Content */}
                                {projectData.content && (
                                    <div className="mb-6">
                                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Content</h2>
                                        {Array.isArray(projectData.content) ? (
                                            <div className="space-y-4">
                                                {projectData.content.map((block, index) => (
                                                    <div key={block.id || index} className="border-l-4 border-blue-500 pl-4">
                                                        {block.type === 'text' && (
                                                            <div
                                                                className="prose max-w-none"
                                                                dangerouslySetInnerHTML={{ __html: block.content }}
                                                            />
                                                        )}
                                                        {block.type === 'image' && (
                                                            <div className="my-4">
                                                                <img
                                                                    src={block.content}
                                                                    alt={`Project content ${index + 1}`}
                                                                    className="max-w-full h-auto rounded-lg shadow-md"
                                                                    loading="lazy"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div
                                                className="prose max-w-none"
                                                dangerouslySetInnerHTML={{ __html: projectData.content }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Image Lightbox Modal */}
            {isImageModalOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
                    onClick={closeImageModal}
                >
                    <button
                        onClick={closeImageModal}
                        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
                    >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <img
                        src={selectedImage}
                        alt="Full size"
                        className="max-w-full max-h-full object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </>
    )
}

export default OurProjectCommercialDesignDetails